import { NavigationContext } from '@react-navigation/native';
import React, { useContext } from 'react';
import { View, SxProp, Pressable } from 'dripsy';

import { UniSkeleton } from '@/design-system/components/cards';
import { TextAmount, TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { Icon } from '@/design-system/components/icons/Icon';
import { useStore } from 'store/store';

interface DisplayTexts {
  show: {
    netAssetValue: string;
    lastDayEarnings: string;
    sevenDayEarnings: string;
  };
  hidden: {
    netAssetValue: string;
    lastDayEarnings: string;
    sevenDayEarnings: string;
  };
}
export interface UniStakingOverviewCardProps {
  overviewData?: {
    netAssetValue: string;
    lastDayEarnings: string;
    sevenDayEarnings: string;
    currency?: string;
  };
  loading?: boolean;
}

export const UniStakingOverviewCard: React.FC<UniStakingOverviewCardProps> = (props) => {
  const navigation = useContext(NavigationContext);

  const { loading, overviewData } = props;

  if (loading) {
    return <UniStakingOverviewLoadingCard />;
  }

  const { showAmount, changeShowAmount } = useStore();

  return (
    <View sx={sxStyles.container}>
      {/* Overview Header */}
      <View sx={overviewHeaderSxStyles.container}>
        <TextTitle semiBold>Overview</TextTitle>
        <View sx={overviewHeaderSxStyles.buttonGroup}>
          <Pressable onPress={() => navigation?.navigate('ActivityHistory')}>
            <View sx={overviewHeaderSxStyles.button}>
              <Icon name="barGraph" />
            </View>
          </Pressable>
          <Pressable onPress={() => navigation?.navigate('ActivityHistory')}>
            <View sx={overviewHeaderSxStyles.button}>
              <Icon name="history" />
            </View>
          </Pressable>
        </View>
      </View>
      {/* Net Asset Valuse */}
      <View sx={netAssetValueSxStyles.container}>
        <View sx={netAssetValueSxStyles.header}>
          <TextBody secondary label>
            Net Asset Value
          </TextBody>
          <Pressable onPress={() => changeShowAmount(!showAmount)}>
            <View sx={netAssetValueSxStyles.showAmountBtn}>
              <Icon name={showAmount ? 'show' : 'hide'} />
            </View>
          </Pressable>
        </View>
        <TextAmount size="H5" weight="bold" currency={overviewData?.currency || ''} shouldHide>
          {overviewData?.netAssetValue || ' - '}
        </TextAmount>
      </View>
      {/* Earnings */}
      <View sx={earningsSxStyles.container}>
        <View sx={earningsSxStyles.containerLeft}>
          <TextCaption secondary label>
            Last Day Earnings
          </TextCaption>
          <TextAmount size="textBody" weight="semiBold" currency={overviewData?.currency || ''} success shouldHide>
            {overviewData?.lastDayEarnings || ' - '}
          </TextAmount>
        </View>
        <View sx={earningsSxStyles.containerRight}>
          <TextCaption secondary label>
            7-Day Earnings
          </TextCaption>
          <TextAmount size="textBody" weight="semiBold" currency={overviewData?.currency || ''} success shouldHide>
            {overviewData?.sevenDayEarnings || ' - '}
          </TextAmount>
        </View>
      </View>
    </View>
  );
};

const UniStakingOverviewLoadingCard: React.FC<any> = () => {
  return (
    <View sx={sxStyles.container}>
      {/* Overview Header */}
      <View sx={overviewHeaderSxStyles.container}>
        <UniSkeleton style={{ height: 19, width: 150 }} />
        <View sx={overviewHeaderSxStyles.buttonGroup}>
          <UniSkeleton style={{ height: 19, width: 100 }} />
        </View>
      </View>
      {/* Net Asset Valuse */}
      <View sx={netAssetValueSxStyles.container}>
        <View sx={netAssetValueSxStyles.header}>
          <UniSkeleton style={{ height: 17, width: 100 }} />
        </View>
        <UniSkeleton style={{ height: 24, width: 200 }} />
      </View>
      {/* Earnings */}
      <View sx={earningsSxStyles.container}>
        <View sx={earningsSxStyles.containerLeft}>
          <UniSkeleton style={{ marginBottom: 4, height: 15, width: 100 }} />
          <UniSkeleton style={{ height: 15, width: 150 }} />
        </View>
        <View sx={earningsSxStyles.containerRight}>
          <UniSkeleton style={{ marginBottom: 4, height: 15, width: 100 }} />
          <UniSkeleton style={{ height: 15, width: 150 }} />
        </View>
      </View>
    </View>
  );
};

const sxStyles: { [key: string]: SxProp } = {
  container: {
    paddingX: '$3',
    pb: 20
  }
};

const overviewHeaderSxStyles: { [key: string]: SxProp } = {
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  buttonGroup: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  button: {
    marginLeft: 5,
    paddingX: 5,
    borderRadius: 4,
    backgroundColor: '$backgroundPrimary',
    borderColor: '$backgroundPrimary',
    borderWidth: 1,
    justifyContent: 'center',
    alignItems: 'center'
  }
};

const netAssetValueSxStyles: { [key: string]: SxProp } = {
  container: {
    marginY: 20
  },
  header: {
    mb: 4,
    flexDirection: 'row',
    alignItems: 'flex-end'
  },
  showAmountBtn: {
    marginLeft: 10
  }
};

const earningsSxStyles: { [key: string]: SxProp } = {
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  containerLeft: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start'
  },
  containerRight: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-end'
  }
};
